import type {
    SearchFilterDefinition,
    SearchFilterOption,
    SearchFilterSelection,
    SearchFilterValue,
} from 'shared/search-engine';

export interface MathiaGroupLevelSource {
    level?: string | number | null;
    archived?: boolean;
    type?: string | number | null;
}

export function buildMathiaActivitySearchInitialSelection(
    groups: readonly MathiaGroupLevelSource[],
    definitions: readonly SearchFilterDefinition<SearchFilterValue>[],
    classGroupType: string | number,
): SearchFilterSelection<SearchFilterValue> {
    const levelOptions = definitions.find(definition => definition.key === 'level')?.options ?? [];
    const selectedLevels = groups
        .filter(group => !group.archived && Number(group.type) === Number(classGroupType))
        .map(group => findMatchingLevelOption(group.level, levelOptions)?.value)
        .filter((level): level is SearchFilterValue => level !== undefined && level !== null);
    const uniqueLevels = [...new Map(selectedLevels.map(level => [String(level), level])).values()];

    return uniqueLevels.length > 0 ? {level: uniqueLevels} : {};
}

export function findMatchingLevelOption(
    groupLevel: MathiaGroupLevelSource['level'],
    options: readonly SearchFilterOption<SearchFilterValue>[],
): SearchFilterOption<SearchFilterValue> | undefined {
    if (groupLevel === undefined || groupLevel === null || groupLevel === '') {
        return undefined;
    }

    const comparableLevel = String(groupLevel);

    return options.find(option => [
        option.value,
        option.label,
        ...getMetadataComparableValues(option.metadata),
    ].some(value => value !== undefined && value !== null && String(value) === comparableLevel));
}

function getMetadataComparableValues(metadata: unknown): unknown[] {
    if (!metadata || typeof metadata !== 'object') {
        return [];
    }

    const record = metadata as Record<string, unknown>;
    return [record.id, record.value, record.name, record.label, record.level];
}
